import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi } from '../services/api';

const AuthContext = createContext(null);
const BANK_ACCOUNT_PROMPT_SKIPPED_KEY = 'bankAccountNumberPromptSkipped';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showProfilePicturePrompt, setShowProfilePicturePrompt] = useState(false);

  // Check for existing session on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const response = await authApi.getCurrentUser();
          if (response.data) {
            setUser(response.data.user);
            setMember(response.data.member);
            setIsAuthenticated(true);
          }
        } catch (error) {
          console.error('Session validation failed:', error);
          // Clear invalid session
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('member');
          sessionStorage.removeItem(BANK_ACCOUNT_PROMPT_SKIPPED_KEY);
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const response = await authApi.getCurrentUser();
      if (response.data) {
        setUser(response.data.user);
        setMember(response.data.member);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        localStorage.setItem('member', JSON.stringify(response.data.member));
      }
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  }, []);

  const login = useCallback((authData) => {
    sessionStorage.removeItem(BANK_ACCOUNT_PROMPT_SKIPPED_KEY);
    localStorage.setItem('token', authData.token);
    localStorage.setItem('user', JSON.stringify(authData.user));
    if (authData.member) {
      localStorage.setItem('member', JSON.stringify(authData.member));
      setMember(authData.member);
    }
    setUser(authData.user);
    setIsAuthenticated(true);
    setShowProfilePicturePrompt(!authData.member?.profile_picture);
    // Refresh member from GET /me so company_logo (profile picture) is available on first load
    refreshUser();
  }, [refreshUser]);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('member');
      sessionStorage.removeItem(BANK_ACCOUNT_PROMPT_SKIPPED_KEY);
      setUser(null);
      setMember(null);
      setIsAuthenticated(false);
      setShowProfilePicturePrompt(false);
      window.location.replace(`${window.location.origin}/`);
    }
  }, []);

  const updateMember = useCallback((memberData) => {
    setMember(memberData);
    localStorage.setItem('member', JSON.stringify(memberData));
  }, []);

  const value = {
    user,
    member,
    loading,
    isAuthenticated,
    login,
    logout,
    updateMember,
    refreshUser,
    showProfilePicturePrompt,
    setShowProfilePicturePrompt
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
